/**
 * useLiveVisualChannel - Runtime bootstrap and visual websocket lifecycle
 *
 * Keeps the live websocket/session transport separate from App.tsx so the core
 * live controller can be reused without dragging the hosted shell component
 * along with it.
 */

import { Dispatch, MutableRefObject, SetStateAction, useEffect, useRef } from 'react';

import type { AvatarSessionContext, AvatarSessionStatus } from '../../session';
import {
  LiveModeClient,
  createLiveSessionContext,
  isRetryableLiveSessionError,
  type LiveBlendshapeFrame
} from '../../session/liveMode';
import { RuntimeSessionStreamClient } from '../../session/runtimeSessionStream';
import { fusionFor, inferSentimentSource } from '../../loop/sentimentFusion';
import { shouldSkipVoiceLegTranscriptFinal } from '../utils/liveControllerUtils';

type LivePromptOption = { label: string; value: string };

type LivePromptField = {
  name: string;
  type: 'text' | 'number' | 'date' | 'phone' | 'email' | 'currency' | 'time' | 'yes_no' | 'select';
  label: string;
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    required?: boolean;
    min?: number;
    max?: number;
    pattern?: string;
    minLength?: number;
    maxLength?: number;
  };
};

interface LiveVisualChannelRefs {
  liveClientRef: MutableRefObject<LiveModeClient | null>;
  liveSessionStreamRef: MutableRefObject<RuntimeSessionStreamClient | null>;
  liveSessionContextRef: MutableRefObject<AvatarSessionContext | null>;
  liveRetryTimerRef: MutableRefObject<number | null>;
  liveRetryCountRef: MutableRefObject<number>;
  liveVoiceRetryTimerRef: MutableRefObject<number | null>;
  liveVoiceRetryCountRef: MutableRefObject<number>;
  liveAvatarModelIdRef: MutableRefObject<string | null>;
  liveManualHangupRef: MutableRefObject<boolean>;
  livePromptTextRef: MutableRefObject<string>;
  livePlaybackCountdownRef: MutableRefObject<number | null>;
  livePromptListeningRef: MutableRefObject<boolean>;
  liveSpeakingRef: MutableRefObject<boolean>;
  liveThinkingRef: MutableRefObject<boolean>;
  livePromptAudioRequestRef: MutableRefObject<number>;
  livePromptAudioDedupRef: MutableRefObject<{ key: string | null; timestamp: number }>;
  liveAutoListenPendingRef: MutableRefObject<boolean>;
  isCallActiveRef: MutableRefObject<boolean>;
}

export interface UseLiveVisualChannelOptions {
  refs: LiveVisualChannelRefs;
  liveModeActive: boolean;
  liveConnectRequested: boolean;
  liveSessionToken?: string;
  liveVoiceCallEnabled: boolean;
  handleInputExternally?: boolean;
  isWebrtcCall: boolean;
  /**
   * True for the runtime host's `/visual` route — Visual IVR. The phone
   * plays the prompts; the browser only renders the avatar lip-syncing to
   * the runtime's blendshape stream. Used here to suppress the browser-side
   * TTS fetch (`POST /runtime-api/talk`) that would otherwise duplicate the
   * audio the user is already hearing on their phone.
   */
  isVisualOnly: boolean;
  isAvatarReady: boolean;
  runtimeUrl?: string | null;
  projectTokenCamlin?: string;
  builderDiagramId?: string;
  liveSessionContext: AvatarSessionContext | null;
  linkedAvatarConfig: Record<string, unknown> | null;
  buildContextualChatLiveRuntimeContext: (context: AvatarSessionContext | null) => Record<string, unknown> | null;
  resolveLinkedAvatarModelId: (
    explicitAvatarModelId?: string | null,
    avatarConfig?: Record<string, unknown> | null
  ) => string | null;
  clearLiveAutoListenTimer: () => void;
  clearLivePlaybackCountdown: () => void;
  clearLiveSpeakingTimeout: () => void;
  clearLiveVoiceRetryTimer: () => void;
  dispatchLiveBlendshapePlayback: (
    frames: LiveBlendshapeFrame[],
    context?: AvatarSessionContext | null
  ) => void;
  dispatchAvatarEvent: (eventName: string, detail: unknown) => void;
  finishLiveSpeaking: (context?: AvatarSessionContext | null) => void;
  playLivePromptAudio: (text: string) => Promise<void>;
  shouldSuppressInitialActiveListenPrompt: () => boolean;
  stopLiveSyntheticAudioSync: (finalCurrentTimeSeconds?: number) => void;
  setLiveSessionContext: (context: AvatarSessionContext | null) => void;
  setLinkedAvatarModelId: (value: string | null) => void;
  setLinkedAvatarConfig: (value: Record<string, unknown> | null) => void;
  setLiveConnectionError: (value: string | null) => void;
  setLiveAssistantText: (value: string) => void;
  setDisplayText: (value: string) => void;
  setLiveChatText: (value: string) => void;
  setLiveChatEventId: Dispatch<SetStateAction<number>>;
  setLivePromptFields: (fields: LivePromptField[]) => void;
  setLivePromptOptions: (options: LivePromptOption[]) => void;
  setLiveUserTranscript: (value: string) => void;
  setLiveUserTranscriptFinal: (value: boolean) => void;
  setLiveTranscriptEventId: Dispatch<SetStateAction<number>>;
  setIsCallActive: (value: boolean) => void;
  setIsWebrtcCall: (value: boolean) => void;
  setIsInVoiceCall: (value: boolean) => void;
  setLiveStatus: (
    status: AvatarSessionStatus,
    context?: AvatarSessionContext | null,
    lastError?: string
  ) => void;
}

const VALID_FIELD_TYPES = new Set([
  'text', 'number', 'date', 'phone', 'email', 'currency', 'time', 'yes_no', 'select'
]);

function normalizePromptFields(
  rawFields: Array<Record<string, unknown>> | undefined
): LivePromptField[] {
  if (!Array.isArray(rawFields)) {
    return [];
  }

  return rawFields
    .map((field) => {
      const name = typeof field?.name === 'string' ? field.name.trim() : '';
      const label = typeof field?.label === 'string' ? field.label.trim() : '';
      const rawType = typeof field?.type === 'string' ? field.type.trim() : 'text';
      const type = VALID_FIELD_TYPES.has(rawType)
        ? rawType as LivePromptField['type']
        : 'text';

      if (!name || !label) {
        return null;
      }

      const result: LivePromptField = { name, type, label };
      if (typeof field.placeholder === 'string') result.placeholder = field.placeholder;
      if (Array.isArray(field.options)) {
        result.options = field.options.filter(
          (o: unknown): o is { label: string; value: string } =>
            typeof o === 'object' && o !== null &&
            typeof (o as Record<string, unknown>).label === 'string' &&
            typeof (o as Record<string, unknown>).value === 'string'
        );
      }
      if (field.validation && typeof field.validation === 'object') {
        result.validation = field.validation as LivePromptField['validation'];
      }
      return result;
    })
    .filter((f): f is LivePromptField => f !== null);
}

/**
 * Branch names / values the runtime generates for system-level exits (timeout,
 * recognizer failures, graceful fallbacks). These are plumbing for the flow
 * author, NOT real choices the caller should see as button labels. If the
 * runtime ever starts sending an `isSystem: true` flag on options this filter
 * can be removed in favour of that.
 */
const SYSTEM_BRANCH_NAMES = new Set([
  'default',
  'no-input-max',
  'no-input',
  'no-match-max',
  'no-match',
  'error',
  'timeout',
  'retry',
  'fallback',
  'exit',
  'end',
  'end-session'
]);

function isSystemBranchOption(label: string, value: string): boolean {
  const normalize = (str: string) =>
    str.toLowerCase().replace(/[\s_]+/g, '-').trim();
  const normalizedLabel = normalize(label);
  const normalizedValue = normalize(value);
  return (
    SYSTEM_BRANCH_NAMES.has(normalizedLabel)
    || SYSTEM_BRANCH_NAMES.has(normalizedValue)
  );
}

function normalizePromptOptions(
  rawOptions: Array<{ label?: string; value?: string; isSystem?: boolean } | null | undefined> | undefined
): LivePromptOption[] {
  if (!Array.isArray(rawOptions)) {
    return [];
  }

  return rawOptions
    .map((option) => {
      const label = typeof option?.label === 'string' ? option.label.trim() : '';
      const value = typeof option?.value === 'string' ? option.value.trim() : '';
      if (!label || !value) {
        return null;
      }

      // Hide system / fallback branches from the caller — they're wiring
      // for the flow author (timeouts, retries, error exits) and should
      // never appear as tappable buttons. Forward-compat: if the runtime
      // sets `isSystem: true` we honour that directly.
      if (option?.isSystem === true) {
        return null;
      }
      if (isSystemBranchOption(label, value)) {
        return null;
      }

      return { label, value };
    })
    .filter((option): option is LivePromptOption => option !== null);
}

export function useLiveVisualChannel({
  refs,
  liveModeActive,
  liveConnectRequested,
  liveSessionToken,
  liveVoiceCallEnabled,
  handleInputExternally,
  isWebrtcCall,
  isVisualOnly,
  isAvatarReady,
  runtimeUrl,
  projectTokenCamlin,
  builderDiagramId,
  liveSessionContext,
  linkedAvatarConfig,
  buildContextualChatLiveRuntimeContext,
  resolveLinkedAvatarModelId,
  clearLiveAutoListenTimer,
  clearLivePlaybackCountdown,
  clearLiveSpeakingTimeout,
  clearLiveVoiceRetryTimer,
  dispatchLiveBlendshapePlayback,
  dispatchAvatarEvent,
  finishLiveSpeaking,
  playLivePromptAudio,
  shouldSuppressInitialActiveListenPrompt,
  stopLiveSyntheticAudioSync,
  setLiveSessionContext,
  setLinkedAvatarModelId,
  setLinkedAvatarConfig,
  setLiveConnectionError,
  setLiveAssistantText,
  setDisplayText,
  setLiveChatText,
  setLiveChatEventId,
  setLivePromptFields,
  setLivePromptOptions,
  setLiveUserTranscript,
  setLiveUserTranscriptFinal,
  setLiveTranscriptEventId,
  setIsCallActive,
  setIsWebrtcCall,
  setIsInVoiceCall,
  setLiveStatus
}: UseLiveVisualChannelOptions): void {
  const {
    liveClientRef,
    liveSessionStreamRef,
    liveSessionContextRef,
    liveRetryTimerRef,
    liveRetryCountRef,
    liveVoiceRetryTimerRef,
    liveVoiceRetryCountRef,
    liveAvatarModelIdRef,
    liveManualHangupRef,
    livePromptTextRef,
    livePlaybackCountdownRef,
    livePromptListeningRef,
    liveSpeakingRef,
    liveThinkingRef,
    livePromptAudioRequestRef,
    livePromptAudioDedupRef,
    liveAutoListenPendingRef,
    isCallActiveRef
  } = refs;

  const linkedAvatarConfigRef = useRef<Record<string, unknown> | null>(linkedAvatarConfig);

  useEffect(() => {
    linkedAvatarConfigRef.current = linkedAvatarConfig ?? null;
  }, [linkedAvatarConfig]);

  useEffect(() => {
    if (!liveModeActive || !liveClientRef.current) {
      return;
    }

    const contextualRuntimeContext = buildContextualChatLiveRuntimeContext(liveSessionContextRef.current);
    if (!contextualRuntimeContext) {
      return;
    }

    liveClientRef.current.sendSessionContext(contextualRuntimeContext);
  }, [
    buildContextualChatLiveRuntimeContext,
    liveClientRef,
    liveModeActive,
    liveSessionContext,
    liveSessionContextRef
  ]);

  useEffect(() => {
    if (!liveModeActive || !liveConnectRequested || !liveSessionToken || !runtimeUrl) {
      return;
    }

    // Browser SIP/live sessions are created by CamlinVoice after the SIP leg
    // is accepted. Opening /visual-channel earlier races that bootstrap and
    // the runtime correctly rejects visual.link with session-not-found.
    if (liveVoiceCallEnabled && !isWebrtcCall) {
      return;
    }

    let cancelled = false;
    const currentContext = liveSessionContextRef.current;
    if (!currentContext) {
      return;
    }

    const contextualRuntimeContext = buildContextualChatLiveRuntimeContext(currentContext);
    const shouldBootstrapRuntimeSession = !liveVoiceCallEnabled && contextualRuntimeContext !== null;

    const ensureRuntimeSession = async () => {
      if (!shouldBootstrapRuntimeSession) {
        liveSessionStreamRef.current?.disconnect();
        liveSessionStreamRef.current = null;
        return;
      }

      const bootstrapContext: Record<string, unknown> = {
        ...(contextualRuntimeContext ?? {}),
        ...(currentContext.sessionId ? { sessionId: currentContext.sessionId } : {}),
        ...(currentContext.handoffToken ? { handoffToken: currentContext.handoffToken } : {}),
        ...(currentContext.userId ? { userId: currentContext.userId } : {}),
        ...(currentContext.tenantId ? { tenantId: currentContext.tenantId } : {}),
        ...(liveAvatarModelIdRef.current ? { avatarModelId: liveAvatarModelIdRef.current } : {})
      };

      if (!liveSessionStreamRef.current) {
        liveSessionStreamRef.current = new RuntimeSessionStreamClient(runtimeUrl);
      }

      await liveSessionStreamRef.current.connect({
        sessionId: currentContext.sessionId,
        handoffToken: currentContext.handoffToken,
        projectToken: typeof currentContext.projectToken === 'string' ? currentContext.projectToken : projectTokenCamlin,
        diagramId: typeof currentContext.diagramId === 'string' ? currentContext.diagramId : builderDiagramId,
        tenantId: typeof currentContext.tenantId === 'string' ? currentContext.tenantId : undefined,
        channel: 'visual',
        context: bootstrapContext
      });
    };

    const connectVisualChannel = async () => {
      if (cancelled) {
        return;
      }

      try {
        await ensureRuntimeSession();
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message = error instanceof Error ? error.message : 'Unable to start live session';
        setLiveConnectionError(message);
        setLiveStatus('error', currentContext, message);
        setLiveAssistantText(message);
        return;
      }

      if (cancelled) {
        return;
      }

      const client = new LiveModeClient({
        runtimeUrl,
        handoffToken: liveSessionToken,
        onLinked: (sessionId, diagramId, nextAvatarModelId, nextAvatarConfig) => {
          const linkedContext = createLiveSessionContext({
            ...currentContext,
            sessionId,
            handoffToken: sessionId,
            diagramId: diagramId || currentContext.diagramId,
            metadata: {
              ...(currentContext.metadata ?? {}),
              liveMode: true
            }
          }, builderDiagramId);

          setLiveSessionContext(linkedContext);
          liveSessionContextRef.current = linkedContext;
          setLiveStatus('ready', linkedContext);
          const resolvedAvatarModelId = resolveLinkedAvatarModelId(nextAvatarModelId, nextAvatarConfig);
          console.log('[LiveMode] linked avatar payload', {
            nextAvatarModelId,
            resolvedAvatarModelId,
            nextAvatarConfig
          });
          setLinkedAvatarModelId(resolvedAvatarModelId);
          setLinkedAvatarConfig(nextAvatarConfig ?? null);
          (window as any).__CAMLIN_AVATAR_SESSION_CONTEXT__ = linkedContext;
          dispatchAvatarEvent('avatar:session:context', linkedContext);

          const linkedRuntimeContext = buildContextualChatLiveRuntimeContext(linkedContext);
          if (linkedRuntimeContext) {
            liveClientRef.current?.sendSessionContext(linkedRuntimeContext);
          }
        },
        onSwitchAvatar: (nextAvatarModelId) => {
          console.log('[LiveMode] switchAvatar received', { nextAvatarModelId });
          setLinkedAvatarModelId(
            resolveLinkedAvatarModelId(nextAvatarModelId, linkedAvatarConfigRef.current)
          );
        },
        onPrompt: (prompt) => {
          clearLiveVoiceRetryTimer();
          liveVoiceRetryCountRef.current = 0;
          setLiveConnectionError(null);
          clearLivePlaybackCountdown();
          const promptText = typeof prompt.text === 'string' ? prompt.text.trim() : '';
          const fullChatText = typeof prompt.visual?.fullText === 'string'
            ? prompt.visual.fullText.trim()
            : '';
          const spokenPromptText = promptText || fullChatText;
          if (spokenPromptText) {
            livePromptTextRef.current = spokenPromptText;
            setDisplayText(spokenPromptText);
            setLiveAssistantText(spokenPromptText);
          }
          const chatText = fullChatText || promptText;
          if (chatText) {
            setLiveChatText(chatText);
            setLiveChatEventId((prev) => prev + 1);
            // Notify the avatar so it can react to keywords/emoji in the
            // bot's spoken text (Avatar.tsx listens for `avatar:chat:content`
            // and runs emoji-derived expression overlays). Ported from the
            // retired builder VisualChannel which was the only caller of
            // this event before the runtime host took over.
            dispatchAvatarEvent('avatar:chat:content', {
              text: chatText,
              source: 'bot'
            });
          }
          const normalizedOptions = normalizePromptOptions(prompt.options);
          setLivePromptOptions(normalizedOptions);
          const visualData = prompt.visual as Record<string, unknown> | undefined;
          const uiContent = visualData?.uiContent as Record<string, unknown> | undefined;
          const rawFields = uiContent?.fields as Array<Record<string, unknown>> | undefined;
          const normalizedFields = normalizePromptFields(rawFields);
          // If the prompt has tappable option buttons (Yes/No, branch
          // choices, menu options), suppress any DTMF-style number input
          // field the runtime attached. The options ARE the way to
          // answer — doubling up with a freeform number field is noise
          // and regularly causes users to type "1" when they meant to
          // tap the "Report Absence" button. The runtime currently
          // scrapes "press 1" / "say 2" out of the prompt text and
          // inserts a digits field; on the visual surface that scrape
          // is redundant because we've rendered the branches as buttons.
          // Keep non-number fields (text / date / phone / email /
          // select / yes_no) — those are genuine structured input even
          // when options are present (e.g. "Choose: Yes / No" AND a
          // "notes" textarea). Narrow suppression to `type === 'number'`.
          const filteredFields = normalizedOptions.length > 0
            ? normalizedFields.filter((f) => f.type !== 'number')
            : normalizedFields;
          setLivePromptFields(filteredFields);
          // Trigger browser-side TTS only when there is no other audio
          // source. There are three live-mode shapes:
          //   - Visual IVR        (isVisualOnly=true,  liveVoiceCallEnabled=false): phone plays audio
          //   - Browser SIP/live  (isVisualOnly=false, liveVoiceCallEnabled=true ): SIP plays audio
          //   - Browser-only chat (isVisualOnly=false, liveVoiceCallEnabled=false): browser TTS plays
          // Only the third shape needs the browser to fetch /talk and play
          // it locally. Without the isVisualOnly guard the Visual IVR client
          // ALSO fetches /talk and plays it through laptop speakers, so the
          // user hears every prompt twice — once on the phone and once
          // through the browser, slightly out of sync with the avatar's
          // lip-sync (which is driven by the runtime's blendshape stream).
          if (spokenPromptText && !liveVoiceCallEnabled && !isVisualOnly) {
            void playLivePromptAudio(spokenPromptText);
          }
          if (!prompt.listening) {
            setLiveUserTranscript('');
            setLiveUserTranscriptFinal(false);
          }
          dispatchAvatarEvent('avatar:prompt', {
            ...prompt,
            sessionId: currentContext.sessionId ?? currentContext.handoffToken
          });
        },
        onListening: (active) => {
          if (active) {
            clearLiveVoiceRetryTimer();
            liveVoiceRetryCountRef.current = 0;
            setLiveConnectionError(null);
          }
          livePromptListeningRef.current = active;
          if (active) {
            liveThinkingRef.current = false;
            dispatchAvatarEvent('avatar:thinking', { thinking: false });
          }

          if (!liveSpeakingRef.current && !liveThinkingRef.current) {
            dispatchAvatarEvent('avatar:listening', { listening: active });
            setLiveStatus(active ? 'listening' : 'ready', currentContext);
          }
        },
        onThinking: (thinking) => {
          liveThinkingRef.current = thinking;
          dispatchAvatarEvent('avatar:thinking', { thinking });
          if (thinking) {
            clearLivePlaybackCountdown();
            clearLiveSpeakingTimeout();
            liveSpeakingRef.current = false;
            stopLiveSyntheticAudioSync();
            setDisplayText('Thinking...');
            setLiveAssistantText('Thinking...');
            dispatchAvatarEvent('avatar:listening', { listening: false });
            setLiveStatus('thinking', currentContext);
          } else if (livePromptTextRef.current) {
            setDisplayText(livePromptTextRef.current);
            setLiveAssistantText(livePromptTextRef.current);
            if (livePromptListeningRef.current && isCallActiveRef.current && !liveSpeakingRef.current) {
              dispatchAvatarEvent('avatar:listening', { listening: true });
              setLiveStatus('listening', currentContext);
            } else if (!liveSpeakingRef.current) {
              setLiveStatus('ready', currentContext);
            }
          }
        },
        onTranscript: (transcript, isFinal) => {
          if (shouldSkipVoiceLegTranscriptFinal({
            isFinal,
            isVisualOnly,
            handleInputExternally,
            liveVoiceCallEnabled,
            browserSttListening: livePromptListeningRef.current
          })) {
            return;
          }
          if (transcript) {
            setDisplayText(transcript);
          }
          setLiveUserTranscript(transcript);
          setLiveUserTranscriptFinal(isFinal);
          if (isFinal && transcript.trim()) {
            setLiveTranscriptEventId((prev) => prev + 1);
            fusionFor(window).markTurnBoundary();
          }
          dispatchAvatarEvent('avatar:transcript', {
            text: transcript,
            isFinal,
            sessionId: currentContext.sessionId ?? currentContext.handoffToken
          });
        },
        onSpeaking: (speaking) => {
          if (!speaking) {
            // Barge-in / playback interrupted (visual.playback.stopped only
            // fires on an interrupt — speech barge-in, DTMF, or visual stop —
            // never on a natural prompt end). Halt any in-flight viseme
            // animation IMMEDIATELY so the mouth stops the moment the prompt is
            // cut off, instead of the renderer's local-clock fallback playing
            // out the remaining buffered frames until the next prompt. Then
            // transition to listening/ready via finishLiveSpeaking.
            dispatchAvatarEvent('avatar:stop', { stopReason: 'playback-stopped' });
            finishLiveSpeaking(currentContext);
            return;
          }
          if (shouldSuppressInitialActiveListenPrompt()) {
            console.log('[LiveMode] Suppressing initial prompt speaking state until Listen is pressed');
            return;
          }
          liveSpeakingRef.current = true;
          liveThinkingRef.current = false;
          setDisplayText(livePromptTextRef.current || 'Speaking...');
          setLiveAssistantText(livePromptTextRef.current || 'Speaking...');
          setLiveStatus('speaking', currentContext);
        },
        onBlendshapes: (frames) => {
          console.log('[LiveMode] onBlendshapes received', { frameCount: frames.length });
          if (shouldSuppressInitialActiveListenPrompt()) {
            console.log('[LiveMode] Suppressing initial prompt blendshapes until Listen is pressed');
            return;
          }
          dispatchLiveBlendshapePlayback(frames, currentContext);
        },
        onPlaybackCountdown: (delayMs, frames) => {
          console.log('[LiveMode] onPlaybackCountdown received', { delayMs, frameCount: frames.length });
          clearLivePlaybackCountdown();

          if (shouldSuppressInitialActiveListenPrompt()) {
            console.log('[LiveMode] Suppressing delayed prompt blendshapes until Listen is pressed');
            return;
          }

          if (delayMs <= 0) {
            dispatchLiveBlendshapePlayback(frames, currentContext);
            return;
          }

          livePlaybackCountdownRef.current = window.setTimeout(() => {
            livePlaybackCountdownRef.current = null;
            dispatchLiveBlendshapePlayback(frames, currentContext);
          }, delayMs);
        },
        onAudioPosition: ({ currentTime, duration, isPlaying }) => {
          // SINGLE SOURCE OF TRUTH for animation timing in any live-mode
          // session that has CamlinVoice in the audio path. This is the
          // exact same mechanism Visual IVR has been using successfully
          // since v1.6 (Mar 2026) and which we've now extended to the
          // browser SIP/WebRTC live mode (Apr 2026) so both paths agree.
          //
          // Why this is the right model:
          //   CamlinVoice owns the actual audio clock — it controls when
          //   `channel.play()` starts on Asterisk and tracks elapsed time
          //   from there. Every 200ms it publishes that elapsed time as
          //   `voice.audio.position`. The runtime forwards as
          //   `visual.audio.position` to the visual websocket. We feed
          //   it into `externalAudioTimeRef` via `avatar:audioTimeSync`
          //   and the renderer's `getAnimationTime` snaps the playhead.
          //   It's wall-clock-accurate to within one tick (200ms) and
          //   `getAnimationTime` extrapolates the gap with a 50ms cap.
          //
          // History: this callback was disabled in Feb 2026 because
          // re-syncing on every tick caused morph-target jitter on long
          // prompts. At that time `Avatar.tsx#getAnimationTime` would
          // re-anchor to every external sync sample with no smoothing.
          // It was hardened since (50ms extrapolation cap, pause
          // detection, sync-only mixer touch). The synthetic clock
          // running in parallel (the actual cause of the jitter) is now
          // also fully disabled in `useLivePromptPlayback` for any
          // CamlinVoice-driven path. External sync is the only timing
          // source the renderer sees, exactly like Visual IVR.
          //
          // Audio pipeline latency compensation:
          //   `playbackStartTime` is when Asterisk's `channel.play()`
          //   callback fires — i.e. when CamlinVoice STARTS pushing
          //   audio out, NOT when the audio reaches the listener's ears.
          //   The actual playback lags by a small fixed amount that
          //   depends on transport:
          //     - Visual IVR (phone): RTP/PSTN transit ~10-50ms +
          //       phone codec + earpiece scheduling ~10-50ms
          //     - Browser WebRTC live: RTP transit ~10-50ms + browser
          //       jitter buffer ~50-100ms + codec/scheduling ~10-50ms
          //   Both work out to ~50-150ms; 50ms is a conservative single
          //   value that covers good-network cases for either transport
          //   without making visemes visibly LAG audio (the symptom of
          //   over-compensation; Keith reported this on prod when the
          //   constant was 150ms).
          //
          //   If visemes start LAGGING audio in the field, reduce this
          //   constant; if they still lead, increase it.
          const AUDIO_PIPELINE_LATENCY_S = 0.05;
          const compensatedCurrentTime = Math.max(0, currentTime - AUDIO_PIPELINE_LATENCY_S);
          dispatchAvatarEvent('avatar:audioTimeSync', {
            currentTime: compensatedCurrentTime,
            duration,
            isPlaying
          });
        },
        onSentiment: (sentiment) => {
          const source = inferSentimentSource(sentiment);
          fusionFor(window).dispatch(window, sentiment, source);
        },
        onEscalation: (escalation) => {
          // Sentiment-decline trend triggered (sprint 20.5). Surface as a
          // window event so hosted shells can switch to "connecting you
          // to a person" UI, route to a human, or log to analytics.
          // Restored after a regression where the canonical client
          // silently dropped `visual.escalation` messages.
          if (escalation.triggered) {
            console.warn('[LiveMode] Escalation triggered:', escalation.reason, '(trend:', escalation.trend + ')');
          }
          dispatchAvatarEvent('avatar:escalation', escalation);
        },
        onNodeChanged: (nodeId, nodeType, nodeTitle) => {
          // Flow advanced to a new node. Restored after a regression
          // where the canonical client silently dropped
          // `visual.node_changed` messages. Used by debug overlays and
          // analytics; cheap to fire.
          dispatchAvatarEvent('avatar:node:changed', { nodeId, nodeType, nodeTitle });
        },
        onIdentityRequired: (info) => {
          // Slice M4h-6d-4 (May 12 2026) — surface the identity-gate
          // UI affordance via a window event so the host shell
          // (App.tsx for the runtime host, AvatarOverlayPanel for
          // builder embeds) can render a confirmation modal. The
          // companion audio prompt arrives via `onPrompt` independently;
          // this event is the trigger for the visible "Confirm" button.
          console.log('[LiveMode] avatar:identity:required dispatched', info);
          dispatchAvatarEvent('avatar:identity:required', info);
        },
        onCallEnded: (reason) => {
          const reconnectQueued = Boolean(liveVoiceRetryTimerRef.current) && !liveManualHangupRef.current;
          clearLiveAutoListenTimer();
          clearLivePlaybackCountdown();
          clearLiveSpeakingTimeout();
          liveAutoListenPendingRef.current = false;
          livePromptListeningRef.current = false;
          liveSpeakingRef.current = false;
          liveThinkingRef.current = false;
          stopLiveSyntheticAudioSync();
          setIsCallActive(false);
          setIsWebrtcCall(false);
          setIsInVoiceCall(false);
          setLivePromptFields([]);
          setLivePromptOptions([]);
          setLiveUserTranscript('');
          setLiveUserTranscriptFinal(false);
          dispatchAvatarEvent('avatar:listening', { listening: false });
          dispatchAvatarEvent('avatar:thinking', { thinking: false });

          if (reconnectQueued) {
            setDisplayText('Reconnecting live session...');
            setLiveAssistantText('Reconnecting live session...');
            setLiveStatus('connecting', currentContext, reason);
            return;
          }

          clearLiveVoiceRetryTimer();
          liveVoiceRetryCountRef.current = 0;
          setDisplayText('Live session ended');
          setLiveAssistantText('Live session ended');
          setLiveStatus('ended', currentContext, reason);
        },
        onError: (message, terminal) => {
          clearLiveAutoListenTimer();
          clearLivePlaybackCountdown();
          clearLiveSpeakingTimeout();
          liveAutoListenPendingRef.current = false;
          liveSpeakingRef.current = false;
          liveThinkingRef.current = false;
          stopLiveSyntheticAudioSync();
          setIsCallActive(false);
          setLivePromptFields([]);
          setLivePromptOptions([]);
          setLiveUserTranscript('');
          setLiveUserTranscriptFinal(false);

          if (terminal) {
            // Reconnect attempts exhausted — the websocket is gone for good
            // and nothing will revive this session. Without a status flip
            // the UI kept looking connected forever (status stayed 'ready'
            // while the channel was dead — Aug 29 2026 honesty gap). 'ended'
            // (not 'error') so call surfaces show the calm "Call ended"
            // treatment; the message still lands in lastError for the
            // health badge / diagnostics.
            dispatchAvatarEvent('avatar:listening', { listening: false });
            dispatchAvatarEvent('avatar:thinking', { thinking: false });
            setIsWebrtcCall(false);
            setIsInVoiceCall(false);
            setDisplayText('Live session ended');
            setLiveAssistantText('Live session ended');
            setLiveStatus('ended', currentContext, message);
          }
        }
      });

      // Contextual-greeting seed (Jul 28 2026): hand the contextual-chat
      // runtime context (greeting / userDisplayName / conversationHistory /
      // contextSummary / specialist persona) to the client BEFORE connect.
      // The client is not linked yet so this queues the context, and
      // LiveModeClient.openSocket now embeds it in the visual.link payload
      // itself — the first message the runtime sees — rather than waiting
      // for the post-link visual.context.update replay, which arrives after
      // the runtime has already spoken its templated greeting.
      if (contextualRuntimeContext) {
        client.sendSessionContext(contextualRuntimeContext);
      }

      liveClientRef.current = client;
      client.connect().then(() => {
        if (cancelled || liveClientRef.current !== client) {
          client.disconnect();
          return;
        }

        liveRetryCountRef.current = 0;
        setIsCallActive(true);
        setLiveConnectionError(null);
        setLiveStatus('ready', currentContext);
        setLiveAssistantText(livePromptTextRef.current || 'Live session ready');
        if (isAvatarReady) {
          client.sendReady();
        }
      }).catch((error) => {
        if (cancelled || liveClientRef.current !== client) {
          return;
        }

        liveClientRef.current = null;
        setIsCallActive(false);
        const message = error instanceof Error ? error.message : 'Unable to connect live session';

        if (isRetryableLiveSessionError(error)) {
          if (shouldBootstrapRuntimeSession) {
            liveSessionStreamRef.current?.disconnect();
            liveSessionStreamRef.current = null;
          }
          setLiveConnectionError(null);
          liveRetryCountRef.current += 1;
          setLiveStatus('connecting', currentContext, message);
          setLiveAssistantText('Reconnecting live session...');
          liveRetryTimerRef.current = window.setTimeout(() => {
            void connectVisualChannel();
          }, Math.min(1000 * liveRetryCountRef.current, 4000));
          return;
        }

        setLiveConnectionError(message);
        setLiveStatus('error', currentContext, message);
        setLiveAssistantText(message);
      });
    };

    void connectVisualChannel();

    return () => {
      cancelled = true;
      if (liveRetryTimerRef.current) {
        clearTimeout(liveRetryTimerRef.current);
        liveRetryTimerRef.current = null;
      }
      clearLiveVoiceRetryTimer();
      clearLiveAutoListenTimer();
      clearLivePlaybackCountdown();
      clearLiveSpeakingTimeout();
      liveAutoListenPendingRef.current = false;
      livePromptListeningRef.current = false;
      liveSpeakingRef.current = false;
      liveThinkingRef.current = false;
      stopLiveSyntheticAudioSync();
      liveRetryCountRef.current = 0;
      liveVoiceRetryCountRef.current = 0;
      liveManualHangupRef.current = false;
      livePromptAudioRequestRef.current += 1;
      livePromptAudioDedupRef.current = { key: null, timestamp: 0 };
      liveSessionStreamRef.current?.disconnect();
      liveSessionStreamRef.current = null;
      liveClientRef.current?.disconnect();
      liveClientRef.current = null;
    };
  }, [
    buildContextualChatLiveRuntimeContext,
    builderDiagramId,
    clearLiveAutoListenTimer,
    clearLivePlaybackCountdown,
    clearLiveSpeakingTimeout,
    clearLiveVoiceRetryTimer,
    dispatchAvatarEvent,
    dispatchLiveBlendshapePlayback,
    finishLiveSpeaking,
    isAvatarReady,
    isWebrtcCall,
    isCallActiveRef,
    liveAutoListenPendingRef,
    liveClientRef,
    liveConnectRequested,
    liveManualHangupRef,
    liveModeActive,
    livePlaybackCountdownRef,
    livePromptAudioDedupRef,
    livePromptAudioRequestRef,
    livePromptListeningRef,
    livePromptTextRef,
    liveRetryCountRef,
    liveRetryTimerRef,
    liveSessionContextRef,
    liveSessionStreamRef,
    liveSessionToken,
    liveSpeakingRef,
    liveThinkingRef,
    liveAvatarModelIdRef,
    liveVoiceCallEnabled,
    liveVoiceRetryCountRef,
    liveVoiceRetryTimerRef,
    playLivePromptAudio,
    projectTokenCamlin,
    resolveLinkedAvatarModelId,
    runtimeUrl,
    setDisplayText,
    setIsCallActive,
    setIsInVoiceCall,
    setIsWebrtcCall,
    setLinkedAvatarConfig,
    setLinkedAvatarModelId,
    setLiveAssistantText,
    setLiveChatEventId,
    setLiveChatText,
    setLiveConnectionError,
    setLivePromptFields,
    setLivePromptOptions,
    setLiveSessionContext,
    setLiveStatus,
    setLiveTranscriptEventId,
    setLiveUserTranscript,
    setLiveUserTranscriptFinal,
    shouldSuppressInitialActiveListenPrompt,
    stopLiveSyntheticAudioSync
  ]);

  useEffect(() => {
    if (isAvatarReady) {
      liveClientRef.current?.sendReady();
    }
  }, [isAvatarReady, liveClientRef]);
}

export default useLiveVisualChannel;
